import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { useQueries } from '@tanstack/react-query';
import L from 'leaflet';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import 'leaflet/dist/leaflet.css';
import { escapeHtml } from './ClusterMap';
import {
  GEOJSON_BASE_PATH,
  type DealerWithNuts,
  type RegionalProvince,
} from './useRegional';
import type { PerformanceCategory, ThresholdInfo } from './useTakataDealers';

/**
 * The NUTS3 choropleth — `_Map.cshtml` plus the map half of `stla_regional.js`.
 *
 * Unlike the dealer maps this one draws polygons rather than pins, so there is no
 * marker cluster and nothing to share with `ClusterMap` beyond HTML escaping. The
 * boundary files are the same static assets the Razor page fetched, per country,
 * from `/mystery/geoJson/regions/`; they are megabytes each and identical between
 * waves, so they are cached indefinitely rather than refetched.
 */

/** Europe, at the view `stla_regional.js` configured. */
const INITIAL_CENTER: [number, number] = [50.0, 10.0];
const INITIAL_ZOOM = 4;
const MIN_ZOOM = 3;
const MAX_ZOOM = 15;

/** Zoom cap when framing a clicked province, so a small one does not fill the map. */
const PROVINCE_MAX_ZOOM = 7;

const COLORS = {
  /** Province whose dealers booked nothing — distinct from having no dealers. */
  noAppointments: '#9ca3af',
  /** Province with no dealers at all. */
  noData: '#e2e8f0',
  excellent: '#22c55e',
  good: '#eab308',
  average: '#f97316',
  critical: '#ef4444',
};

export interface RegionalMapProps {
  provinces: RegionalProvince[];
  dealers: DealerWithNuts[];
  categories: Record<string, PerformanceCategory>;
  thresholdsByCountry: Record<string, ThresholdInfo[]>;
  /** ISO code of the country filter; empty shows every country. */
  selectedCountry: string;
  onProvinceClick: (province: RegionalProvince) => void;
  labels: {
    country: string;
    totalDealers: string;
    avgWaitTime: string;
    totalCalls: string;
    days: string;
    noAppointments: string;
  };
  /** Extra bottom padding when framing a province, for the panel over the map. */
  bottomPadding?: number;
  resizeToken?: unknown;
}

/**
 * Fetches one country's province boundaries.
 *
 * The United Kingdom is served as either `UK_` or `GB_` depending on when the
 * file was generated, so a miss on the first falls back to the second rather than
 * leaving the country unpainted.
 */
async function fetchProvinceGeoJson(countryCode: string): Promise<FeatureCollection> {
  const attempts =
    countryCode === 'UK'
      ? [`${GEOJSON_BASE_PATH}UK_provinces.geojson`, `${GEOJSON_BASE_PATH}GB_provinces.geojson`]
      : [`${GEOJSON_BASE_PATH}${countryCode}_provinces.geojson`];

  let lastStatus = 0;
  for (const url of attempts) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (response.ok) return (await response.json()) as FeatureCollection;
    lastStatus = response.status;
  }

  throw new Error(`No province boundaries for ${countryCode} (HTTP ${lastStatus})`);
}

/**
 * The colour a province's average wait earns under its own country's thresholds.
 *
 * Category 0 is skipped: it is the no-appointment case, not a day cutoff. The
 * hard-coded fallbacks only apply to a country with no thresholds configured at
 * all, which is what the Razor version did too.
 */
function colorByWaitDays(
  days: number,
  countryCode: string,
  thresholdsByCountry: RegionalMapProps['thresholdsByCountry'],
  categories: RegionalMapProps['categories'],
): string {
  const thresholds = (thresholdsByCountry[countryCode] ?? [])
    .filter((threshold) => threshold.categoryIndex !== 0)
    .sort((a, b) => a.categoryIndex - b.categoryIndex);

  if (thresholds.length === 0) {
    if (days <= 5.0) return COLORS.excellent;
    if (days <= 10.0) return COLORS.good;
    if (days <= 14.0) return COLORS.average;
    return COLORS.critical;
  }

  // The last band has no upper bound: anything that got past the others is in it.
  const match =
    thresholds.find((threshold) => days <= threshold.maxDays) ?? thresholds[thresholds.length - 1];

  return categories[String(match.categoryIndex)]?.backgroundColor ?? COLORS.critical;
}

interface ProvinceLayersProps extends Omit<RegionalMapProps, 'resizeToken'> {
  geoJsonByCountry: Map<string, FeatureCollection>;
}

function ProvinceLayers({
  geoJsonByCountry,
  provinces,
  dealers,
  categories,
  thresholdsByCountry,
  selectedCountry,
  onProvinceClick,
  labels,
  bottomPadding = 0,
}: ProvinceLayersProps) {
  const map = useMap();

  // Callbacks bound into Leaflet layers outlive the render that created them, so
  // they read the current values through refs rather than closing over stale ones.
  const clickRef = useRef(onProvinceClick);
  clickRef.current = onProvinceClick;
  const paddingRef = useRef(bottomPadding);
  paddingRef.current = bottomPadding;

  const provinceByNuts = useMemo(() => {
    const byNuts = new Map<string, RegionalProvince>();
    for (const province of provinces) {
      if (province.nuts3Code) byNuts.set(province.nuts3Code, province);
    }
    return byNuts;
  }, [provinces]);

  /**
   * Provinces whose dealers exist but booked nothing. An average of zero days is
   * otherwise indistinguishable from an excellent one, so the category is what
   * decides the grey.
   */
  const noAppointmentNuts = useMemo(() => {
    const codes = new Set<string>();
    for (const dealer of dealers) {
      if (dealer.categoryIndex === 0 && dealer.nuts3Code) codes.add(dealer.nuts3Code);
    }
    return codes;
  }, [dealers]);

  const styleFor = useMemo(
    () => (feature?: Feature<Geometry>) => {
      const nutsId = feature?.properties?.NUTS_ID as string | undefined;
      const province = nutsId ? provinceByNuts.get(nutsId) : undefined;

      if (!province) {
        return {
          fillColor: COLORS.noData,
          fillOpacity: 0.3,
          color: '#cbd5e1',
          weight: 1,
          opacity: 0.5,
        };
      }

      const fillColor =
        province.averageWaitDays === 0 && noAppointmentNuts.has(province.nuts3Code)
          ? COLORS.noAppointments
          : colorByWaitDays(
              province.averageWaitDays,
              province.country,
              thresholdsByCountry,
              categories,
            );

      return {
        fillColor,
        fillOpacity: 0.7,
        color: '#1e293b',
        weight: 1.5,
        opacity: 0.6,
      };
    },
    [provinceByNuts, noAppointmentNuts, thresholdsByCountry, categories],
  );

  const tooltipHtml = useMemo(
    () => (province: RegionalProvince) => {
      const noAppointments =
        province.averageWaitDays === 0 && noAppointmentNuts.has(province.nuts3Code);

      const stats = noAppointments
        ? `<div style="color: #9ca3af; font-weight: 600;">${escapeHtml(labels.noAppointments)}</div>`
        : `<div><strong>${escapeHtml(labels.avgWaitTime)}:</strong> ${province.averageWaitDays.toFixed(1)} ${escapeHtml(labels.days)}</div>
           <div><strong>${escapeHtml(labels.totalCalls)}:</strong> ${escapeHtml(province.totalCalls)}</div>`;

      return `
        <div style="padding: 8px;">
          <h4 style="margin: 0 0 8px 0; font-weight: 600;">${escapeHtml(province.nuts3Name)}</h4>
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div><strong>${escapeHtml(labels.country)}:</strong> ${escapeHtml(province.country)}</div>
            <div><strong>${escapeHtml(labels.totalDealers)}:</strong> ${escapeHtml(province.totalDealers)}</div>
            ${stats}
          </div>
        </div>`;
    },
    [noAppointmentNuts, labels],
  );

  // One Leaflet layer per country, rebuilt when the data behind the colours
  // changes. Country visibility is a separate effect so filtering does not
  // re-parse megabytes of boundaries.
  const layers = useMemo(() => {
    const byCountry = new Map<string, L.GeoJSON>();

    for (const [countryCode, geoJson] of geoJsonByCountry) {
      byCountry.set(
        countryCode,
        L.geoJSON(geoJson, {
          style: styleFor,
          onEachFeature: (feature, layer) => {
            const nutsId = feature.properties?.NUTS_ID as string | undefined;
            const province = nutsId ? provinceByNuts.get(nutsId) : undefined;

            layer.on({
              mouseover: (event) => {
                const target = event.target as L.Path;
                target.setStyle({ weight: 3, color: '#0ea5e9', fillOpacity: 0.85 });
                target.bringToFront();
                if (province) {
                  L.popup().setLatLng(event.latlng).setContent(tooltipHtml(province)).openOn(map);
                }
              },
              mouseout: (event) => {
                (event.target as L.Path).setStyle(styleFor(feature));
              },
              click: (event) => {
                if (!province) return;
                clickRef.current(province);

                // Frame the province above the panel that is about to cover the
                // bottom of the map.
                const bounds = (event.target as L.FeatureGroup).getBounds?.();
                if (bounds?.isValid()) {
                  map.fitBounds(bounds, {
                    paddingTopLeft: [10, 10],
                    paddingBottomRight: [10, paddingRef.current + 20],
                    maxZoom: PROVINCE_MAX_ZOOM,
                  });
                }
              },
            });
          },
        }),
      );
    }

    return byCountry;
  }, [geoJsonByCountry, styleFor, provinceByNuts, tooltipHtml, map]);

  useEffect(() => {
    return () => {
      for (const layer of layers.values()) map.removeLayer(layer);
    };
  }, [map, layers]);

  useEffect(() => {
    for (const [countryCode, layer] of layers) {
      if (!selectedCountry || countryCode === selectedCountry) {
        layer.addTo(map);
      } else {
        map.removeLayer(layer);
      }
    }
  }, [map, layers, selectedCountry]);

  return null;
}

/** Tells Leaflet to re-measure after the surrounding layout changed. */
function ResizeOnToken({ token }: { token: unknown }) {
  const map = useMap();

  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), 350);
    return () => window.clearTimeout(timer);
  }, [map, token]);

  return null;
}

export function RegionalMap({ resizeToken, ...layerProps }: RegionalMapProps) {
  // Only the countries the wave actually covers, so no request is made for
  // boundaries nothing would be drawn on.
  const countryCodes = useMemo(
    () =>
      Array.from(
        new Set(layerProps.provinces.map((province) => province.country).filter(Boolean)),
      ).sort(),
    [layerProps.provinces],
  );

  const geoJsonQueries = useQueries({
    queries: countryCodes.map((countryCode) => ({
      queryKey: ['stla', 'geojson', countryCode] as const,
      queryFn: () => fetchProvinceGeoJson(countryCode),
      // Boundaries are static assets: fetch once per session, never revalidate.
      staleTime: Infinity,
      gcTime: Infinity,
      retry: false,
    })),
  });

  const loading = geoJsonQueries.some((query) => query.isPending);

  // A country whose file is missing is skipped rather than failing the map — the
  // other countries still paint, which is what the Razor version did by logging
  // and carrying on.
  const geoJsonByCountry = useMemo(() => {
    const byCountry = new Map<string, FeatureCollection>();
    countryCodes.forEach((countryCode, index) => {
      const data = geoJsonQueries[index]?.data;
      if (data) byCountry.set(countryCode, data);
    });
    return byCountry;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodes, geoJsonQueries.map((query) => query.status).join()]);

  return (
    <div className="map-container">
      <MapContainer
        id="regionalMap"
        center={INITIAL_CENTER}
        zoom={INITIAL_ZOOM}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        zoomControl
        maxBoundsViscosity={0.5}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
          maxZoom={19}
        />
        <ProvinceLayers {...layerProps} geoJsonByCountry={geoJsonByCountry} />
        <ResizeOnToken token={resizeToken} />
      </MapContainer>

      {loading && (
        <div className="regional-loading-overlay">
          <div className="spinner" />
        </div>
      )}
    </div>
  );
}
